import ListingFilterDefinition, {
  ListingFilterDefinitionOption,
  ListingFilterDefinitionOptionBadgeData,
  ListingFilterDefinitionOptionSelectData,
  ListingFilterDefinitionOptionSelectDataType,
} from "../../../../../common/components/listing/filter/types/listing-filter-definition";
import ListingSortDefinition, {
  ListingSortDefinitionOption,
} from "../../../../../common/components/listing/filter/types/listing-sort-definition";
import UserStatus from "../../../../../common/types/user-status";
import userHelper from "../../../../../common/utils/user/user.helper";
import userTranslationsHelper from "../../../../../languages/user-translations.helper";
import PassengerListingFilter, {
  PassengerListingAddressFilter,
  PassengerListingCargoCompanyFilter,
  PassengerListingDispatchFilter,
  PassengerListingEmailFilter,
  PassengerListingFirstNameFilter,
  PassengerListingLastNameFilter,
  PassengerListingStatusFilter,
  PassengerListingUsernameFilter,
} from "../common/types/passenger-listing-filter";
import PassengerListingFilterType from "../common/types/passenger-listing-filter-type";
import PassengerListingSortKey from "../common/types/passenger-listing-sort-key";

const passengerListingFilterHelper = () => {
  const translations = userTranslationsHelper.getPassengerListingTranslations();

  const createBadgeData = (
    value: string,
    textTemplate: string,
    titleTemplate: string
  ): ListingFilterDefinitionOptionBadgeData => {
    return {
      text: textTemplate.replace("#{query}", value),
      title: titleTemplate.replace("#{query}", value),
    };
  };

  const getSelectDataByType = (
    filterType: PassengerListingFilterType,
    query: string
  ) => {
    const options: Record<
      PassengerListingFilterType,
      (query: string) => ListingFilterDefinitionOptionSelectData
    > = {
      [PassengerListingFilterType.FIRST_NAME]: getSelectDataForFirstName,
      [PassengerListingFilterType.LAST_NAME]: getSelectDataForLastName,
      [PassengerListingFilterType.ACTIVITY_STATUS]: getSelectDataForStatus,
      [PassengerListingFilterType.USERNAME]: getSelectDataForUserName,
      [PassengerListingFilterType.EMAIL]: getSelectDataForEmailAddress,
      [PassengerListingFilterType.DISPATCH]: getSelectDataForDispatch,
      [PassengerListingFilterType.CARGO_COMPANY]: getSelectDataForCargoCompany,
      [PassengerListingFilterType.ADDRESS]: getSelectDataForAddress,
    };

    const result = options[filterType](query);

    return result;
  };

  const getSelectDataForFirstName = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.firstNameTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForLastName = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.lastNameTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForStatus = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
      options: [
        {
          label:
            translations.filters.search.activityStatusTemplateLabel.replace(
              "#{query}",
              userHelper.getActivityStatusText(UserStatus.ACTIVE)
            ),
          value: UserStatus.ACTIVE,
        },
        {
          label:
            translations.filters.search.activityStatusTemplateLabel.replace(
              "#{query}",
              userHelper.getActivityStatusText(UserStatus.DELETED)
            ),
          value: UserStatus.DELETED,
        },
      ],
    };
  };

  const getSelectDataForUserName = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.usernameTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForEmailAddress = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.emailTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForDispatch = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.dispatchTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForCargoCompany = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.cargoCompanyTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForAddress = (
    query: string
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.addressTemplateLabel.replace(
            `#{query}`,
            query
          ),
          value: query,
        },
      ],
    };
  };

  const getBadgeForFirstName = (
    filterValue: PassengerListingFirstNameFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.firstNameLabel,
      translations.filters.firstNameTitle
    );
  };

  const getBadgeForLastName = (
    filterValue: PassengerListingLastNameFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.lastNameLabel,
      translations.filters.lastNameTitle
    );
  };

  const getBadgeForActivityStatus = (
    filterValue: PassengerListingStatusFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    const filterValueText = userHelper.getActivityStatusText(filterValue);

    return createBadgeData(
      filterValueText,
      translations.filters.activityStatusLabel,
      translations.filters.activityStatusTitle
    );
  };

  const getBadgeForEmailAddress = (
    filterValue: PassengerListingLastNameFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.emailLabel,
      translations.filters.emailTitle
    );
  };

  const getBadgeForCargoCompany = (
    filterValue: PassengerListingCargoCompanyFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.cargoCompanyLabel,
      translations.filters.cargoCompanyTitle
    );
  };

  const getBadgeForUsername = (
    filterValue: PassengerListingUsernameFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.usernameLabel,
      translations.filters.usernameTitle
    );
  };

  const getBadgeForDispatch = (
    filterValue: PassengerListingDispatchFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.dispatchLabel,
      translations.filters.dispatchTitle
    );
  };

  const getBadgeForAddress = (
    filterValue: PassengerListingAddressFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.addressLabel,
      translations.filters.addressTitle
    );
  };

  const getBadgeDefinitionByType = (
    filterType: PassengerListingFilterType,
    filterValue: PassengerListingFilter["value"]
  ): ListingFilterDefinitionOptionBadgeData => {
    const options: Record<
      PassengerListingFilterType,
      () => ListingFilterDefinitionOptionBadgeData
    > = {
      [PassengerListingFilterType.FIRST_NAME]: () =>
        getBadgeForFirstName(
          filterValue as PassengerListingFirstNameFilter["value"]
        ),
      [PassengerListingFilterType.LAST_NAME]: () =>
        getBadgeForLastName(
          filterValue as PassengerListingLastNameFilter["value"]
        ),
      [PassengerListingFilterType.EMAIL]: () =>
        getBadgeForEmailAddress(
          filterValue as PassengerListingEmailFilter["value"]
        ),

      [PassengerListingFilterType.ACTIVITY_STATUS]: () =>
        getBadgeForActivityStatus(
          filterValue as PassengerListingStatusFilter["value"]
        ),
      [PassengerListingFilterType.USERNAME]: () =>
        getBadgeForUsername(
          filterValue as PassengerListingUsernameFilter["value"]
        ),
      [PassengerListingFilterType.CARGO_COMPANY]: () =>
        getBadgeForCargoCompany(
          filterValue as PassengerListingCargoCompanyFilter["value"]
        ),
      [PassengerListingFilterType.DISPATCH]: () =>
        getBadgeForDispatch(
          filterValue as PassengerListingDispatchFilter["value"]
        ),
      [PassengerListingFilterType.ADDRESS]: () =>
        getBadgeForAddress(
          filterValue as PassengerListingAddressFilter["value"]
        ),
    };

    return options[filterType]();
  };

  const getFilterDefinitionByType = (
    filterType: PassengerListingFilterType
  ): ListingFilterDefinitionOption<PassengerListingFilter> => {
    return {
      getSelectData: (query: string) => getSelectDataByType(filterType, query),
      getBadgeData: (value) => getBadgeDefinitionByType(filterType, value),
      filterType,
    };
  };

  const getFilterDefinitionOptions =
    (): ListingFilterDefinitionOption<PassengerListingFilter>[] => {
      return Object.values(PassengerListingFilterType).map(
        getFilterDefinitionByType
      );
    };

  const getFilterDefinition =
    (): ListingFilterDefinition<PassengerListingFilter> => {
      return {
        options: getFilterDefinitionOptions(),
      };
    };

  const getSortDefinition = (): ListingSortDefinition => {
    return {
      options: getSortDefinitionOptions(),
    };
  };

  const getSortDefinitionOptions = (): ListingSortDefinitionOption[] => {
    return [
      {
        label: translations.sort.firstNameAscLabel,
        value: PassengerListingSortKey.FIRST_NAME_ASC,
      },
      {
        label: translations.sort.firstNameDescLabel,
        value: PassengerListingSortKey.FIRST_NAME_DESC,
      },
      {
        label: translations.sort.lastNameAscLabel,
        value: PassengerListingSortKey.LAST_NAME_ASC,
      },
      {
        label: translations.sort.lastNameDescLabel,
        value: PassengerListingSortKey.LAST_NAME_DESC,
      },
      {
        label: translations.sort.usernameAscLabel,
        value: PassengerListingSortKey.USERNAME_ASC,
      },
      {
        label: translations.sort.usernameDescLabel,
        value: PassengerListingSortKey.USERNAME_DESC,
      },
    ];
  };

  return { getFilterDefinition, getSortDefinition };
};

export default passengerListingFilterHelper;
